import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, Mail, HelpCircle, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { StandaloneTopBar } from "@/components/Layout/StandaloneTopBar";
import { useQuery, useQueryClient } from "@tanstack/react-query";

const STATUS_VARIANTS: Record<string, "default" | "outline" | "secondary" | "destructive"> = {
  open:        "default",
  in_progress: "secondary",
  resolved:    "outline",
  closed:      "outline",
};

const faqs = [
  {
    question: "How do I connect my social media accounts?",
    answer: "Navigate to Settings > Social Accounts and click 'Connect Account' to authorise access to your social media platforms.",
  },
  {
    question: "What payment methods do you accept?",
    answer: "We accept Mobile Money, credit cards, and bank transfers for premium subscriptions.",
  },
  {
    question: "How can I cancel my subscription?",
    answer: "Go to Billing Information and click 'Cancel Subscription'. Your access will continue until the end of your billing period.",
  },
  {
    question: "Is there a limit on scheduled posts?",
    answer: "Premium plan users have unlimited scheduled posts. Free plan users can schedule up to 10 posts per month.",
  },
];

export default function Support() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Resolve current user + their org_id once
  const { data: context } = useQuery({
    queryKey: ["support-context"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const { data: membership } = await supabase
        .from("organization_members")
        .select("org_id")
        .eq("user_id", user.id)
        .eq("status", "active")
        .limit(1)
        .maybeSingle();
      return { userId: user.id, orgId: membership?.org_id ?? null };
    },
  });

  const { data: tickets, isLoading: loadingTickets } = useQuery({
    queryKey: ["my-support-tickets", context?.userId],
    enabled: !!context?.userId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("support_tickets")
        .select("id, title, status, priority, created_at")
        .eq("submitted_by", context!.userId)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!context?.userId) {
      toast({ title: "Please sign in first", variant: "destructive" });
      return;
    }
    if (!context.orgId) {
      toast({ title: "No organisation found", description: "You must belong to an organisation to submit a ticket.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await (supabase as any)
        .from("support_tickets")
        .insert({
          org_id:       context.orgId,
          submitted_by: context.userId,
          title:        subject.trim(),
          description:  description.trim(),
          priority:     priority || "medium",
          status:       "open",
        });
      if (error) throw error;
      toast({ title: "Ticket submitted", description: "We will get back to you within 24 hours." });
      setSubject(""); setDescription(""); setPriority("");
      qc.invalidateQueries({ queryKey: ["my-support-tickets"] });
    } catch (err: any) {
      toast({ title: "Submission failed", description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-background">
      <StandaloneTopBar backTo="/dashboard" backLabel="Back to Dashboard" />
      <div className="container max-w-5xl py-8 px-6 mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Support Center</h1>
          <p className="text-justify text-muted-foreground">Get help and submit support tickets</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="cursor-pointer hover:border-primary transition-colors">
            <CardContent className="pt-6 text-center">
              <MessageSquare className="w-8 h-8 mx-auto mb-3 text-primary" />
              <h3 className="font-semibold mb-1">Live Chat</h3>
              <p className="text-justify text-sm text-muted-foreground">Chat with our support team</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-primary transition-colors">
            <CardContent className="pt-6 text-center">
              <Mail className="w-8 h-8 mx-auto mb-3 text-primary" />
              <h3 className="font-semibold mb-1">Email Support</h3>
              <p className="text-justify text-sm text-muted-foreground">support@conorbit.com</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-primary transition-colors">
            <CardContent className="pt-6 text-center">
              <HelpCircle className="w-8 h-8 mx-auto mb-3 text-primary" />
              <h3 className="font-semibold mb-1">Help Center</h3>
              <p className="text-justify text-sm text-muted-foreground">Browse help articles</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Submit Ticket Form */}
          <Card>
            <CardHeader>
              <CardTitle>Submit a Ticket</CardTitle>
              <CardDescription>Describe your issue and we will help resolve it</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="subject">Subject</Label>
                  <Input
                    id="subject"
                    placeholder="Brief description of your issue"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="priority">Priority</Label>
                  <Select value={priority} onValueChange={setPriority}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Provide detailed information about your issue"
                    rows={5}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={submitting}>
                  {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Submit Ticket
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="space-y-6">
            {/* My Tickets */}
            <Card>
              <CardHeader>
                <CardTitle>Your Tickets</CardTitle>
                <CardDescription>Track your support requests</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {loadingTickets ? (
                  <div className="flex justify-center py-6">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                ) : !tickets || tickets.length === 0 ? (
                  <p className="text-justify text-sm text-muted-foreground text-center py-4">
                    No tickets yet. Submit one above.
                  </p>
                ) : (
                  tickets.map((ticket: any) => (
                    <div key={ticket.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="text-justify font-medium text-sm">{ticket.title}</p>
                        <p className="text-justify text-xs text-muted-foreground">
                          {new Date(ticket.created_at).toLocaleDateString()} · {ticket.priority}
                        </p>
                      </div>
                      <Badge variant={STATUS_VARIANTS[ticket.status] ?? "outline"}>
                        {ticket.status.replace("_", " ")}
                      </Badge>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* FAQ */}
            <Card>
              <CardHeader>
                <CardTitle>Frequently Asked Questions</CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible>
                  {faqs.map((faq, index) => (
                    <AccordionItem key={index} value={`item-${index}`}>
                      <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                      <AccordionContent className="text-muted-foreground">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
